import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api } from "@/lib/api";

type Check = {
  reviewId: string;
  asin: string;
  amazonReviewId: string;
  title: string | null;
  bodyPreview: string;
  submittedAt: string | null;
  matchedLive: boolean;
  matchKind: "title" | "body" | "reviewerName" | null;
  liveReviewId: string | null;
  liveReviewerName: string | null;
  liveReviewDate: string | null;
  flippedToRemoved: boolean;
};

type Result =
  | { message: string; checks?: never; hitAuthWall?: never; liveReviewCountByAsin?: never }
  | {
      auditId: string;
      asins: string[];
      liveReviewCountByAsin: Record<string, number>;
      hitAuthWall: boolean;
      checks: Check[];
      message?: never;
    };

export function VerifyTakedowns({ auditId, onChange }: { auditId: string; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [applyRemoved, setApplyRemoved] = useState(false);

  async function run() {
    setError(null);
    setResult(null);
    setBusy(true);
    const res = await api<Result>(`/admin/audits/${auditId}/verify-takedowns`, {
      method: "POST",
      body: JSON.stringify({ applyRemovedStatus: applyRemoved }),
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? "Verification failed.");
      return;
    }
    setResult(res.data ?? null);
    if (applyRemoved) onChange();
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Verify takedowns</h3>
          <p className="mt-0.5 text-xs text-slate-600">
            Scrape live Amazon 1-star reviews and content-match against this
            audit's submitted-takedown reviews.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={applyRemoved}
              onChange={(e) => setApplyRemoved(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Flip absent reviews to <code>removed</code>
          </label>
          <Button type="button" size="sm" onClick={run} disabled={busy}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {busy ? "Scraping…" : "Re-check Amazon"}
          </Button>
        </div>
      </div>

      {error && (
        <p className="mt-3 flex items-center gap-1 text-xs text-red-600" role="alert">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      {result && "message" in result && result.message && (
        <p className="mt-3 text-xs text-slate-600">{result.message}</p>
      )}

      {result && "checks" in result && result.checks && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-3 text-xs text-slate-600">
            {result.asins.map((a) => (
              <span key={a}>
                <code>{a}</code>: {result.liveReviewCountByAsin[a] ?? 0} 1★
                live
              </span>
            ))}
            {result.hitAuthWall && (
              <span className="text-red-600">
                Auth wall hit — context may be invalidated.
              </span>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">ASIN</th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Submitted</th>
                  <th className="px-3 py-2 text-left">Match</th>
                  <th className="px-3 py-2 text-left">Live reviewer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.checks.map((c) => (
                  <tr key={c.reviewId}>
                    <td className="px-3 py-2">
                      {c.matchedLive ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" /> Still up
                        </span>
                      ) : c.flippedToRemoved ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Removed (flipped)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <XCircle className="h-3.5 w-3.5" /> Not found
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono">{c.asin}</td>
                    <td className="px-3 py-2 max-w-xs truncate">{c.title || "—"}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {c.submittedAt
                        ? new Date(c.submittedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {c.matchKind ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {c.liveReviewerName ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
